import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import { AdminCargoReturnDto, cargoReturnAdminService } from '../api/adminServices'
import { useToast } from '../components/useToast'

/**
 * Returns awaiting a fault decision (docs/design/return-flow.md §5): damage / missing / other, and unconfirmed
 * returns the Shipper contested. The decision is final: it releases the Shipment's held settlement (Shipper fault:
 * trip + return leg; Freighter fault: the cargo's share is dropped and incident #7 charged) and is scored.
 */
function fmtDistance(m?: number | null): string {
  if (m == null) return 'no fix'
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`
}

export default function CargoReturnsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<{ row: AdminCargoReturnDto; fault: 'SHIPPER' | 'FREIGHTER' } | null>(null)
  const [notes, setNotes] = useState('')

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin-cargo-returns-awaiting'],
    queryFn: () => cargoReturnAdminService.awaitingFault(),
  })
  const rows = data ?? []

  const decide = useMutation({
    mutationFn: (v: { id: string; fault: 'SHIPPER' | 'FREIGHTER'; notes: string }) =>
      cargoReturnAdminService.decideFault(v.id, v.fault, v.notes || undefined),
    onSuccess: () => {
      toast.success('Fault decided. The shipment settlement is released.')
      setTarget(null)
      setNotes('')
      queryClient.invalidateQueries({ queryKey: ['admin-cargo-returns-awaiting'] })
    },
    onError: (e) => toast.errorFrom(e, 'Could not decide the fault'),
  })

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <KeyboardReturnIcon color="primary" />
              <Typography variant="h6">Cargo Returns — awaiting fault decision</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Damaged / missing / other returns and contested unconfirmed returns. Check the driver's photo,
              the receiver's refusal and the location fixes, then decide who pays.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Could not load cargo returns.</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">Nothing awaiting a decision.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Requested</TableCell>
                <TableCell>Cargo / dropoff</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Settled / contest</TableCell>
                <TableCell>Custody fixes</TableCell>
                <TableCell>Evidence</TableCell>
                <TableCell align="right">Decide</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    {new Date(r.requestedAt).toLocaleString()}
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                      job {r.jobId.slice(0, 8)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.cargoUnits ?? ''} {r.cargoType ?? ''}
                    <Typography variant="caption" display="block">
                      {r.dropoffBusinessName || r.dropoffAddress || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.reason} />
                    {r.notes && (
                      <Typography variant="caption" display="block">
                        {r.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.settledBy ?? '—'} · {r.state}
                    {r.refusalSignerName && (
                      <Typography variant="caption" display="block">
                        refusal: {r.refusalSignerName}
                      </Typography>
                    )}
                    {r.contestedAt && (
                      <Typography variant="caption" display="block" color="warning.main">
                        contested {new Date(r.contestedAt).toLocaleString()}
                        {r.contestNotes ? ` — ${r.contestNotes}` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(r.custodyFixes ?? []).map((f) => (
                      <Typography key={f.id} variant="caption" display="block">
                        {f.action}: {fmtDistance(f.distanceFromExpectedM)}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>
                    {(r.documents ?? []).map((d) => (
                      <Typography key={d.id} variant="caption" display="block">
                        {d.documentType}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => setTarget({ row: r, fault: 'SHIPPER' })}>
                        Shipper fault
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => setTarget({ row: r, fault: 'FREIGHTER' })}
                      >
                        Freighter fault
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!target} onClose={() => setTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>{target?.fault === 'SHIPPER' ? 'Shipper fault' : 'Freighter fault'}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {target?.fault === 'SHIPPER'
              ? 'The Shipper pays the trip and the return leg; the Freighter is paid both.'
              : "The cargo's share of the price is neither charged nor paid, and the Freighter is charged the Freighter-fault return penalty."}{' '}
            This is final.
          </DialogContentText>
          <TextField
            label="Decision notes"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)} disabled={decide.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => target && decide.mutate({ id: target.row.id, fault: target.fault, notes })}
            disabled={decide.isPending}
          >
            {decide.isPending ? 'Saving…' : 'Decide'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
